import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FIRST_PAGE, MAX_TAKE_COUNT, MIN_TAKE_COUNT, TAKE_COUNT } from 'hooks/useQueryWithParams'
import { TabsItem } from 'types/tabsItem'
import {
  DEFAULT_STATUS,
  PAGE_QUERY_PARAM,
  PER_PAGE_QUERY_PARAM,
  SEARCH_QUERY_PARAM,
  STATUS_QUERY_PARAM,
  STATUS_TABS,
} from '../constants'
import { getNumberParam } from '../helpers'
import { UpdateTableParams } from '../types'

export const useBlogPostListSearchParams = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const search = searchParams.get(SEARCH_QUERY_PARAM) ?? ''
  const activeStatus = STATUS_TABS.find(({ id }) => id === searchParams.get(STATUS_QUERY_PARAM)) ?? DEFAULT_STATUS
  const page = getNumberParam(searchParams.get(PAGE_QUERY_PARAM), FIRST_PAGE, FIRST_PAGE)
  const take = getNumberParam(searchParams.get(PER_PAGE_QUERY_PARAM), TAKE_COUNT, MIN_TAKE_COUNT, MAX_TAKE_COUNT)

  const updateTableParams = useCallback(
    (updates: UpdateTableParams, resetPage = false) => {
      setSearchParams(
        (currentParams) => {
          const nextParams = new URLSearchParams(currentParams)

          if (updates.search !== undefined) {
            const searchValue = updates.search.trim()
            searchValue ? nextParams.set(SEARCH_QUERY_PARAM, searchValue) : nextParams.delete(SEARCH_QUERY_PARAM)
          }

          if (updates.status !== undefined) {
            updates.status === DEFAULT_STATUS.id
              ? nextParams.delete(STATUS_QUERY_PARAM)
              : nextParams.set(STATUS_QUERY_PARAM, updates.status)
          }

          if (updates.take !== undefined) {
            updates.take === TAKE_COUNT
              ? nextParams.delete(PER_PAGE_QUERY_PARAM)
              : nextParams.set(PER_PAGE_QUERY_PARAM, String(updates.take))
          }

          const nextPage = updates.page ?? (resetPage ? FIRST_PAGE : undefined)

          if (nextPage !== undefined) {
            nextPage === FIRST_PAGE
              ? nextParams.delete(PAGE_QUERY_PARAM)
              : nextParams.set(PAGE_QUERY_PARAM, String(nextPage))
          }

          return nextParams
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const handleSearchChange = useCallback((search: string) => updateTableParams({ search }, true), [updateTableParams])

  const handleStatusChange = useCallback(
    (status: TabsItem) => updateTableParams({ status: status.id }, true),
    [updateTableParams]
  )

  const handlePageChange = useCallback((page: number) => updateTableParams({ page }), [updateTableParams])

  const handleTakeChange = useCallback((take: number) => updateTableParams({ take }, true), [updateTableParams])

  return {
    search,
    activeStatus,
    page,
    take,
    updateTableParams,
    handleSearchChange,
    handleStatusChange,
    handlePageChange,
    handleTakeChange,
  }
}
